export const formatDate = (date, locale = 'it') => {
  const options = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
  return new Date(date).toLocaleDateString(locale === 'it' ? 'it-IT' : 'en-US', options)
}

export const getSportIcon = (sport) => {
  return sport === 'calcio' ? '⚽' : '🎾'
}

export const getRoleIcon = (role) => {
  const icons = {
    goalkeeper: '🧤',
    defender: '🛡️',
    midfielder: '⚡',
    attacker: '🎯',
    left: '⬅️',
    right: '➡️'
  }
  return icons[role] || '👤'
}

export const calculatePoints = (result, violated = false, rules = {}) => {
  const normalizedResult = String(result || '').toLowerCase()
  const winPoints = Number(rules.win ?? 0.02)
  const drawPoints = Number(rules.draw ?? 0.01)
  const lossPoints = Math.abs(Number(rules.loss ?? 0.02))
  const violationDeduction = Number(rules.violation ?? 0.01)

  const base =
    normalizedResult === 'win'
      ? winPoints
      : normalizedResult === 'draw'
        ? drawPoints
        : normalizedResult === 'lose' || normalizedResult === 'loss'
          ? -lossPoints
          : 0

  const violationPenalty = violated ? -Math.abs(violationDeduction) : 0
  return Number((base + violationPenalty).toFixed(4))
}

export const groupByDate = (matches) => {
  const grouped = {}
  matches.forEach(match => {
    const date = new Date(match.date).toDateString()
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(match)
  })
  return grouped
}

export const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

export const isValidPhone = (phone) => {
  const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/
  return regex.test(phone)
}

export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        })
      },
      (error) => {
        reject(error)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    )
  })
}

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export const sortMatchesByDistanceAndTime = (matches, userLocation, referenceDate = new Date()) => {
  if (!userLocation) return [...matches]

  const nowMs = new Date(referenceDate).getTime()

  return [...matches].sort((a, b) => {
    const distA = calculateDistance(userLocation.latitude, userLocation.longitude, a.latitude, a.longitude)
    const distB = calculateDistance(userLocation.latitude, userLocation.longitude, b.latitude, b.longitude)

    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()

    const timeA = Number.isNaN(dateA) ? Number.POSITIVE_INFINITY : Math.abs(dateA - nowMs) / (1000 * 60 * 60)
    const timeB = Number.isNaN(dateB) ? Number.POSITIVE_INFINITY : Math.abs(dateB - nowMs) / (1000 * 60 * 60)

    // Blend distance and time proximity to keep nearby and soon matches at the top.
    const scoreA = distA * 0.75 + timeA * 0.25
    const scoreB = distB * 0.75 + timeB * 0.25

    if (scoreA !== scoreB) return scoreA - scoreB
    if (distA !== distB) return distA - distB
    return dateA - dateB
  })
}

export const getCityFromCoords = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=en`
    )
    const data = await response.json()
    return data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || data.address?.county || ''
  } catch (error) {
    console.error('Error getting city from coordinates:', error)
    return ''
  }
}

export const getDerivedMatchStatus = (matchDate, persistedStatus) => {
  if (persistedStatus === 'cancelled') return 'cancelled'
  if (!matchDate) return 'upcoming'

  const date = new Date(matchDate)
  if (Number.isNaN(date.getTime())) return 'upcoming'

  const now = new Date()
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (isToday) return 'ongoing'
  if (date > now) return 'upcoming'
  return 'completed'
}

/**
 * Max accepted players: prefers match.maxPlayers; if missing, derives from matchType
 * (e.g. 2v2 -> 4, 5v5 -> 10). Returns 0 if unknown (treated as no fixed cap in callers).
 */
export const getMatchRosterCapacity = (match) => {
  const n = Number(match?.maxPlayers)
  if (Number.isFinite(n) && n > 0) return n
  const type = String(match?.matchType || '').trim()
  const m = type.match(/^(\d+)\s*v\s*(\d+)$/i)
  if (m) {
    const a = Number(m[1])
    const b = Number(m[2])
    if (Number.isFinite(a) && Number.isFinite(b)) return a + b
  }
  return 0
}

/**
 * When accepted players reach capacity, remove all `invited` entries from the list.
 */
export const pruneInvitedIfRosterFull = (participants, match) => {
  if (!Array.isArray(participants) || participants.length === 0) return participants
  const cap = getMatchRosterCapacity(match)
  if (cap <= 0) return participants
  const accepted = participants.filter((p) => p.status === 'accepted').length
  if (accepted < cap) return participants
  return participants.filter((p) => p.status !== 'invited')
}